import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Badge,
  Tooltip,
  Tabs,
  Row,
  Col,
  Statistic,
  Form,
  Select,
  DatePicker,
  message,
  Typography,
  Spin,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Api from '../../Network';
import OrderDetailsModal from './OrderDetailsModal';
import CreateOrderModal from './CreateOrderModal';
import { InfiniteSelect } from '../../Component/InfiniteSelect';
import { usePermissions } from '../../contexts/PermissionContext';
import { AnimatedContainer, SectionCard, EmptyState } from '../../Component/ui';
import './style.scss';

const { Text } = Typography;

interface DetailedOrder {
  id: number;
  reference: string;
  is_offer?: boolean;
  offer_id?: number | null;
  offer?: {
    id: number;
    title?: string;
    special_price?: string | number;
  } | null;
  total?: number;
  total_tax?: number;
  total_with_tax?: number;
  customer: {
    id: number;
    name: string;
    phone_number: string;
    tax_number?: string;
  };
  driver?: {
    id: number;
    name: string;
    phone_number: string;
  };
  status: string;
  shipping_status?: string;
  order_items: any[];
  location?: string;
  created_at: string;
  decline_reason?: string;
}

interface OrdersState {
  orders: DetailedOrder[];
  loading: boolean;
  tabLoading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  stats: {
    total: number;
    new: number;
    current: number;
    completed: number;
  };
}

const Orders = () => {
  const { t } = useTranslation();
  const { hasPermission, permissions, roles } = usePermissions();
  const [ordersState, setOrdersState] = useState<OrdersState>({
    orders: [],
    loading: false,
    tabLoading: false,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
    stats: {
      total: 0,
      new: 0,
      current: 0,
      completed: 0,
    },
  });

  const [filters, setFilters] = useState({
    page: 1,
    per_page: 20,
    status: undefined as string | undefined,
    customer_id: '',
    driver_id: '',
    date_from: '',
    date_to: '',
    date_type: undefined as string | undefined,
    approved: undefined as string | undefined,
  });

  const [, setCustomers] = useState([]);
  const [, setDrivers] = useState([]);
  const [activeTab, setActiveTab] = useState('All');
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<DetailedOrder | null>(null);
  const [orderDetailsVisible, setOrderDetailsVisible] = useState(false);
  const [createOrderVisible, setCreateOrderVisible] = useState(false);

  // Mobile detection state
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Check if user has permission to view orders
  const canViewOrders =
    hasPermission('view_orders') ||
    hasPermission('view_order') ||
    hasPermission('orders.read') ||
    hasPermission('orders.view');

  // Log permissions on component mount
  useEffect(() => {
    console.log('=== ORDERS PAGE PERMISSION CHECKS ===');
    console.log('User Roles:', roles);
    console.log('User Permissions:', permissions);
    console.log('Total Permissions:', permissions.length);
    console.log('canViewOrders:', canViewOrders);
    console.log('Permission checks:');
    console.log('  - view_orders:', hasPermission('view_orders'));
    console.log('  - view_order:', hasPermission('view_order'));
    console.log('  - orders.read:', hasPermission('orders.read'));
    console.log('  - orders.view:', hasPermission('orders.view'));
    console.log('=====================================');
  }, [permissions, roles, canViewOrders, hasPermission]);

  useEffect(() => {
    // Only fetch data if user has permission
    if (canViewOrders) {
      console.log('Orders component mounted, fetching data...');
      fetchOrders(filters);
      fetchCustomers();
      fetchDrivers();
    } else {
      console.log('Orders component mounted but user does not have permission to view orders');
    }
  }, [canViewOrders]);

  // Debug logging for orders state
  useEffect(() => {
    console.log('Orders state updated:', ordersState);
  }, [ordersState]);

  const fetchOrders = async (currentFilters: any) => {
    try {
      console.log('Starting fetchOrders with filters:', currentFilters);
      console.log('Auth token:', localStorage.getItem('token'));
      setOrdersState((prev) => ({ ...prev, loading: true, tabLoading: false }));

      // Build query string with proper filter schema
      const queryParams = [];

      // Handle pagination
      if (currentFilters.page) queryParams.push(`page=${currentFilters.page}`);
      if (currentFilters.per_page) queryParams.push(`per_page=${currentFilters.per_page}`);

      // Handle filters with proper schema
      if (currentFilters.status) queryParams.push(`filter[status]=${currentFilters.status}`);
      if (currentFilters.customer_id)
        queryParams.push(`filter[customer]=${currentFilters.customer_id}`);
      if (currentFilters.driver_id) queryParams.push(`filter[driver]=${currentFilters.driver_id}`);
      if (currentFilters.date_from)
        queryParams.push(`filter[date_from]=${currentFilters.date_from}`);
      if (currentFilters.date_to) queryParams.push(`filter[date_to]=${currentFilters.date_to}`);
      if (currentFilters.search) queryParams.push(`filter[search]=${currentFilters.search}`);
      if (currentFilters.approved) queryParams.push(`approved=${currentFilters.approved}`);
      if (currentFilters.date_type) queryParams.push(`date_type=${currentFilters.date_type}`);

      const queryString = queryParams.join('&');

      console.log('Fetching orders with query:', queryString);
      const response = await Api.get(`/api/admin/orders?${queryString}`);

      console.log('=== FULL RESPONSE DEBUG ===');
      console.log('Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response || {}));
      console.log('Response.status:', response?.status);
      console.log('Response.data:', response?.data);
      console.log('Response.data type:', typeof response?.data);
      console.log('Response.data keys:', Object.keys(response?.data || {}));
      console.log('Is response.data an array?', Array.isArray(response?.data));
      console.log('===========================');

      // After Axios interceptor, response is response.data from axios
      // So response = { status: true, code: 200, data: {...paginated data...}, msg: ... }
      // Where data = { data: [...], current_page: 1, per_page: 50, total: 2839, ... }

      if (response && response.status) {
        // Check if response.data exists (the paginated data object)
        const paginatedData = response.data;

        console.log('Paginated data:', paginatedData);
        console.log('Paginated data type:', typeof paginatedData);
        console.log('Paginated data keys:', Object.keys(paginatedData || {}));
        console.log('Is paginatedData an array?', Array.isArray(paginatedData));

        // Extract orders array from paginated data
        let ordersData: any[] = [];

        if (Array.isArray(paginatedData)) {
          // If paginatedData is already an array, use it directly
          ordersData = paginatedData;
          console.log('Using paginatedData as array directly');
        } else if (paginatedData && typeof paginatedData === 'object') {
          // Check for Laravel pagination structure: { data: [...], current_page: 1, ... }
          if (Array.isArray(paginatedData.data)) {
            ordersData = paginatedData.data;
            console.log('Found orders in paginatedData.data');
          } else if (Array.isArray(paginatedData.items)) {
            ordersData = paginatedData.items;
            console.log('Found orders in paginatedData.items');
          } else {
            console.warn('Could not find orders array in paginatedData');
            console.warn('PaginatedData structure:', JSON.stringify(paginatedData, null, 2));
          }
        }

        console.log('Final ordersData:', ordersData);
        console.log('Orders data length:', ordersData.length);
        console.log('First order sample:', ordersData[0]);

        // Handle pagination data from response
        const paginationInfo = {
          current: paginatedData?.current_page || paginatedData?.page || currentFilters.page || 1,
          pageSize:
            paginatedData?.per_page || paginatedData?.perPage || currentFilters.per_page || 20,
          total: paginatedData?.total || ordersData.length,
        };

        console.log('Pagination info:', paginationInfo);

        setOrdersState((prev) => ({
          ...prev,
          orders: ordersData,
          pagination: paginationInfo,
          stats: {
            total: paginationInfo.total,
            new: 0,
            current: 0,
            completed: 0,
          },
          loading: false,
          tabLoading: false,
        }));
      } else {
        console.error('API response not successful:', response);
        setOrdersState((prev) => ({ ...prev, loading: false, tabLoading: false }));
      }
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      console.error('Error type:', typeof error);
      console.error('Error keys:', Object.keys(error || {}));
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error message:', error.message);
      message.error(t('orders.messages.fetchError'));
      setOrdersState((prev) => ({ ...prev, loading: false, tabLoading: false }));
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await Api.get('/api/admin/customers');
      if (response.data.success) {
        setCustomers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await Api.get('/api/admin/drivers');
      if (response.data.success) {
        setDrivers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const handleTabChange = (key: string) => {
    console.log('Tab changed to:', key);
    setActiveTab(key);
    const newFilters = { ...filters, page: 1 };

    switch (key) {
      case 'New':
        newFilters.status = 'New';
        break;
      case 'Current':
        newFilters.status = 'Current';
        break;
      case 'Completed':
        newFilters.status = 'Completed';
        break;
      case 'All':
      default:
        newFilters.status = '';
        break;
    }

    console.log('New filters:', newFilters);
    setFilters(newFilters);
    setOrdersState((prev) => ({ ...prev, tabLoading: true })); // إضافة loading للتاب
    fetchOrders(newFilters);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    const newFilters = {
      ...filters,
      page: page,
      per_page: pageSize,
    };
    setFilters(newFilters);
    fetchOrders(newFilters);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      New: 'blue',
      Approved: 'green',
      Current: 'orange',
      Completed: 'green',
      Declined: 'red',
      Delivered: 'purple',
    };
    return statusColors[status] || 'default';
  };

  // Get shipping status color
  const getShippingStatusColor = (
    status: string | null
  ): 'error' | 'default' | 'processing' | 'success' | 'warning' | undefined => {
    if (!status) return 'default';
    const statusColors: {
      [key: string]: 'error' | 'default' | 'processing' | 'success' | 'warning' | undefined;
    } = {
      Pending: 'processing',
      'In Transit': 'processing',
      Delivered: 'success',
      Failed: 'error',
    };
    return statusColors[status] || 'default';
  };

  const handleViewOrder = (orderId: number) => {
    const order = ordersState.orders.find((o) => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setOrderDetailsVisible(true);
    }
  };

  const handleCreateOrderOpen = () => {
    setCreateOrderVisible(true);
  };

  // Enhanced columns with data-label for mobile responsive design
  const columns: any[] = [
    {
      title: t('orders.columns.id'),
      dataIndex: 'id',
      key: 'id',
      width: 100,
      sorter: true,
      'data-label': t('orders.columns.id'),
    },
    {
      title: t('orders.columns.reference'),
      dataIndex: 'reference',
      key: 'reference',
      width: 150,
      'data-label': t('orders.columns.reference'),
      render: (reference: string) => (
        <div className="reference-cell">
          <Text code className="reference-text-enhanced">
            {reference}
          </Text>
        </div>
      ),
    },
    {
      title: t('orders.columns.customer'),
      dataIndex: ['customer', 'name'],
      key: 'customer',
      width: 200,
      'data-label': t('orders.columns.customer'),
      render: (name: string, record: DetailedOrder) =>
        record.customer ? (
          <div className="customer-info-enhanced">
            <div className="customer-name">{name || record.customer.name || '-'}</div>
            <Text type="secondary" className="customer-phone">
              {record.customer.phone_number || '-'}
            </Text>
            {record.customer.tax_number && (
              <div className="customer-tax">Tax: {record.customer.tax_number}</div>
            )}
          </div>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: t('orders.columns.driver'),
      dataIndex: ['driver', 'name'],
      key: 'driver',
      width: 150,
      'data-label': t('orders.columns.driver'),
      render: (name: string, record: DetailedOrder) =>
        record.driver ? (
          <div className="driver-info-enhanced">
            <span className="driver-name">{name}</span>
          </div>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: t('orders.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      'data-label': t('orders.columns.status'),
      render: (status: string, record: DetailedOrder) => (
        <div className="status-info-enhanced">
          {record.is_offer && (
            <Tag className="offer-order-badge">
              <GiftOutlined className="offer-order-badge__icon" />
              {t('orders.labels.offerOrder', 'Offer Order')}
            </Tag>
          )}
          <Tag
            color={getStatusColor(status)}
            className={`order-status-tag status-${status.toLowerCase()}`}
          >
            {t(`orders.status.${status.toLowerCase()}`, status)}
          </Tag>
          {status === 'Declined' && record.decline_reason && (
            <div className="decline-reason">
              <strong>{t('orders.columns.declineReason')}:</strong> {record.decline_reason}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('orders.columns.shippingStatus'),
      dataIndex: 'shipping_status',
      key: 'shipping_status',
      width: 140,
      'data-label': t('orders.columns.shippingStatus'),
      render: (status: string | null) => (
        <Badge
          status={getShippingStatusColor(status)}
          text={status || '-'}
          className="shipping-status-badge"
        />
      ),
    },
    {
      title: t('orders.columns.total'),
      dataIndex: 'order_items',
      key: 'total',
      width: 120,
      'data-label': t('orders.columns.total'),
      render: (items: any[] | null | undefined, record: DetailedOrder) => {
        const apiTotalRaw = (record as any).total_with_tax;
        if (apiTotalRaw !== undefined && apiTotalRaw !== null && `${apiTotalRaw}` !== '') {
          const apiTotal = Number(apiTotalRaw);
          if (Number.isFinite(apiTotal)) {
            return (
              <Text strong className="order-total-enhanced">
                {apiTotal.toFixed(2)} {t('common.SAR')}
              </Text>
            );
          }
        }
        const subtotal = items?.reduce((sum, item) => sum + (item.total || 0), 0) || 0;
        const withTax = subtotal * (1 + 15 / 100);

        return (
          <Text strong className="order-total-enhanced">
            {withTax.toFixed(2)} {t('common.SAR')}
          </Text>
        );
      },
    },
    {
      title: t('orders.columns.location'),
      dataIndex: 'location',
      key: 'location',
      width: 120,
      'data-label': t('orders.columns.location'),
      render: (location: string) => (
        <div className="location-info-enhanced">
          <EnvironmentOutlined /> {location}
        </div>
      ),
    },
    {
      title: t('orders.columns.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      'data-label': t('orders.columns.createdAt'),
      render: (date: string) => (
        <div className="date-info-enhanced">
          <CalendarOutlined /> {new Date(date).toLocaleDateString()}
        </div>
      ),
    },
    {
      title: t('orders.columns.actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      'data-label': t('orders.columns.actions'),
      render: (_value: any, record: DetailedOrder) => (
        <Space className="action-buttons-enhanced">
          <Tooltip title={t('orders.actions.view')}>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewOrder(record.id)}
              size="small"
              className="view-order-btn"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Check permission first
  if (!canViewOrders) {
    return (
      <div className="orders-page">
        <Card className="order-card-enhanced">
          <Alert
            message={t('permissions.accessDenied') || 'Access Denied'}
            description={
              t('permissions.accessDeniedDescription') ||
              'You do not have permission to view orders'
            }
            type="error"
            showIcon
            style={{ margin: '24px' }}
          />
        </Card>
      </div>
    );
  }

  return (
    <AnimatedContainer className="orders-page" variant="fadeUp">
      <Card className="order-card-enhanced">
        {/* Page Header */}
        <div className="page-header">
          <h2 className="page-title">{t('orders.title')}</h2>
          <p className="page-description">{t('orders.description')}</p>
        </div>

        {/* Statistics */}
        <Row gutter={[16, 16]} className="order-stats-enhanced">
          <Col xs={24} sm={24} md={24} lg={24}>
            <Statistic
              title={t('orders.stats.total', 'Total Orders')}
              value={ordersState.stats.total}
              prefix={<span style={{ fontSize: '24px' }}>📦</span>}
            />
          </Col>
        </Row>

        {/* Info Alert for Empty Tabs */}
        {(activeTab === 'Current' || activeTab === 'Completed') &&
          ordersState.orders.length === 0 &&
          !ordersState.loading && (
            <Alert
              message={t('orders.info.emptyTab')}
              description={t('orders.info.emptyTabDescription')}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className="order-tabs-enhanced"
          items={[
            {
              key: 'All',
              label: (
                <span>
                  {t('orders.tabs.all', 'All Orders')}
                  {ordersState.tabLoading && activeTab === 'All' && (
                    <Spin size="small" style={{ marginLeft: 8 }} />
                  )}
                </span>
              ),
            },
            {
              key: 'New',
              label: (
                <span>
                  {t('orders.tabs.new', 'New Orders')}
                  {ordersState.tabLoading && activeTab === 'New' && (
                    <Spin size="small" style={{ marginLeft: 8 }} />
                  )}
                </span>
              ),
            },
            {
              key: 'Current',
              label: (
                <span>
                  {t('orders.tabs.current', 'Current Orders')}
                  {ordersState.tabLoading && activeTab === 'Current' && (
                    <Spin size="small" style={{ marginLeft: 8 }} />
                  )}
                </span>
              ),
            },
            {
              key: 'Completed',
              label: (
                <span>
                  {t('orders.tabs.completed', 'Completed Orders')}
                  {ordersState.tabLoading && activeTab === 'Completed' && (
                    <Spin size="small" style={{ marginLeft: 8 }} />
                  )}
                </span>
              ),
            },
          ]}
        />

        {/* Actions */}
        <div style={{ marginBottom: 16 }}>
          <Space className="order-actions-enhanced orders-toolbar-actions" size="middle" wrap>
            {(hasPermission('create_order') || hasPermission('orders.create')) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateOrderOpen}
                className="create-order-btn orders-toolbar-btn"
              >
                {t('orders.createOrder.title', 'Create Order')}
              </Button>
            )}
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFiltersVisible(!filtersVisible)}
              type={filtersVisible ? 'primary' : 'default'}
              className="filter-btn orders-toolbar-btn"
            >
              {t('orders.filter', 'Filters')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchOrders(filters)}
              loading={ordersState.loading || ordersState.tabLoading}
              type="default"
              className="refresh-btn orders-toolbar-btn"
            >
              {t('common.refresh', 'Refresh')}
            </Button>
          </Space>
        </div>

        {/* Filters */}
        {filtersVisible && (
          <SectionCard className="premium-filter-form filters-card" style={{ marginBottom: 16 }}>
            <Form
              layout="vertical"
              initialValues={filters}
              onFinish={(values) => {
                // Format dates if they are dayjs objects
                const formattedValues = {
                  ...values,
                  date_from: values.date_from ? dayjs(values.date_from).format('YYYY-MM-DD') : '',
                  date_to: values.date_to ? dayjs(values.date_to).format('YYYY-MM-DD') : '',
                };
                const newFilters = { ...filters, ...formattedValues, page: 1 };
                setFilters(newFilters);
                fetchOrders(newFilters);
              }}
            >
              <Row gutter={[16, 16]} align="bottom">
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="customer_id" label={t('orders.filters.customer')}>
                    <InfiniteSelect
                      queryKey="api/admin/customers"
                      placeholder={t('orders.placeholders.selectCustomer')}
                      showSearch
                      singleMode={true}
                      valueField="id"
                      searchField="name"
                      allowClear
                      labelRender={(option: any) => {
                        const customer = option.item || option;
                        return `${customer.name} - ${customer.phone_number || ''}`;
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="date_type" label={t('orders.dateType', 'Date Type')}>
                    <Select placeholder={t('orders.selectDateType', 'Select Date Type')} allowClear>
                      <Select.Option value="checkout">
                        {t('orders.checkoutDate', 'Checkout Date')}
                      </Select.Option>
                      <Select.Option value="delivered">
                        {t('orders.deliveredDate', 'Delivered Date')}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="date_from" label={t('orders.filters.dateFrom')}>
                    <DatePicker
                      placeholder={t('orders.filters.dateFrom')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="date_to" label={t('orders.filters.dateTo')}>
                    <DatePicker
                      placeholder={t('orders.filters.dateTo')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item name="approved" label={t('orders.filters.approved')}>
                    <Select placeholder={t('orders.filters.approved')} allowClear>
                      <Select.Option value="true">نعم</Select.Option>
                      <Select.Option value="false">لا</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6} className="premium-filter-form__actions-col">
                  <Form.Item className="premium-filter-form__actions" label=" ">
                    <Space>
                      <Button type="primary" htmlType="submit" loading={ordersState.loading}>
                        {t('common.apply', 'Apply Filters')}
                      </Button>
                      <Button
                        type="default"
                        htmlType="button"
                        onClick={() => {
                          const resetFilters = {
                            page: 1,
                            per_page: 20,
                            status: undefined,
                            customer_id: '',
                            driver_id: '',
                            date_from: '',
                            date_to: '',
                            date_type: undefined,
                            approved: undefined,
                          };
                          setFilters(resetFilters);
                          fetchOrders(resetFilters);
                        }}
                      >
                        {t('common.clear', 'Clear Filters')}
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </SectionCard>
        )}

        {/* Mobile Card View or Desktop Table */}
        {isMobileView ? (
          <div className="mobile-table-list">
            {ordersState.orders.length > 0 ? (
              ordersState.orders.map((order) => (
                <Card
                  key={order.id}
                  className={`mobile-card order-card-enhanced ${order.is_offer ? 'offer-order-mobile-card' : ''}`}
                >
                  {columns.map((column) => {
                    if (column.key === 'actions') return null;

                    const dataIndex = column.dataIndex;
                    let rawValue;

                    if (Array.isArray(dataIndex)) {
                      rawValue = dataIndex.reduce((obj, key) => obj?.[key], order);
                    } else if (dataIndex) {
                      rawValue = order[dataIndex as keyof DetailedOrder];
                    }

                    let label = column.title;
                    if (typeof label === 'function') {
                      label = label();
                    }

                    let displayValue;
                    if (column.render) {
                      displayValue = column.render(rawValue, order, 0);
                    } else if (rawValue !== undefined && rawValue !== null) {
                      displayValue = rawValue;
                    } else {
                      displayValue = '-';
                    }

                    return (
                      <div key={column.key} className="mobile-card-field">
                        <span className="mobile-card-label">{label}:</span>
                        <span className="mobile-card-value">{displayValue}</span>
                      </div>
                    );
                  })}

                  {/* Action button for mobile */}
                  <div className="mobile-card-actions">
                    <Button
                      type="primary"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewOrder(order.id)}
                    >
                      {t('orders.actions.view')}
                    </Button>
                  </div>
                </Card>
              ))
            ) : (
              <EmptyState
                size="small"
                description={
                  activeTab === 'Current'
                    ? t('orders.noCurrentOrders')
                    : activeTab === 'Completed'
                      ? t('orders.noCompletedOrders')
                      : t('orders.noOrders')
                }
              />
            )}

            {/* Mobile Pagination */}
            {ordersState.pagination.total > 0 && (
              <div className="mt-pagination">
                <Button
                  size="small"
                  disabled={ordersState.pagination.current <= 1}
                  onClick={() =>
                    handlePageChange(
                      ordersState.pagination.current - 1,
                      ordersState.pagination.pageSize
                    )
                  }
                >
                  {t('table.previous', 'Previous')}
                </Button>
                <span className="mt-page-info">
                  {ordersState.pagination.current} /{' '}
                  {Math.max(
                    1,
                    Math.ceil(ordersState.pagination.total / ordersState.pagination.pageSize)
                  )}
                </span>
                <Button
                  size="small"
                  disabled={
                    ordersState.pagination.current >=
                    Math.ceil(ordersState.pagination.total / ordersState.pagination.pageSize)
                  }
                  onClick={() =>
                    handlePageChange(
                      ordersState.pagination.current + 1,
                      ordersState.pagination.pageSize
                    )
                  }
                >
                  {t('table.next', 'Next')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Desktop Table */
          <Table
            columns={columns}
            dataSource={ordersState.orders}
            rowKey="id"
            rowClassName={(record: DetailedOrder) => (record.is_offer ? 'offer-order-row' : '')}
            loading={ordersState.loading || ordersState.tabLoading}
            pagination={{
              current: ordersState.pagination.current,
              pageSize: ordersState.pagination.pageSize,
              total: ordersState.pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${t('common.showing')} ${range[0]}-${range[1]} ${t('common.of')} ${total} ${t('common.items')}`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange,
            }}
            scroll={{ x: 1200 }}
            className="order-table-enhanced"
            locale={{
              emptyText: (
                <EmptyState
                  size="small"
                  description={
                    activeTab === 'Current'
                      ? t('orders.noCurrentOrders')
                      : activeTab === 'Completed'
                        ? t('orders.noCompletedOrders')
                        : t('orders.noOrders')
                  }
                />
              ),
            }}
          />
        )}
      </Card>

      {/* Modals */}
      {selectedOrder && (
        <OrderDetailsModal
          visible={orderDetailsVisible}
          orderId={selectedOrder.id}
          onClose={() => {
            setOrderDetailsVisible(false);
            setSelectedOrder(null);
          }}
        />
      )}

      <CreateOrderModal
        visible={createOrderVisible}
        onClose={() => setCreateOrderVisible(false)}
        onOrderCreated={() => {
          setCreateOrderVisible(false);
          fetchOrders(filters);
        }}
      />
    </AnimatedContainer>
  );
};

export default Orders;
